import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { addPayment } from "@/lib/api";

export default function AddPaymentDialog({ reservationId, open, onClose, onSaved, defaultRemaining }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(defaultRemaining && defaultRemaining > 0 ? String(defaultRemaining.toFixed(2)) : "");
      setMethod("cash");
      setDateStr(new Date().toISOString().slice(0, 10));
    }
  }, [open, defaultRemaining]);

  const handleSave = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await addPayment({
        reservation_id: reservationId,
        amount: amt,
        method,
        date: dateStr,
      });
      toast.success("Payment recorded");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="add-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Payment</DialogTitle>
          <DialogDescription className="sr-only">Record a payment for this reservation.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="mt-1 h-11 text-lg font-heading"
              data-testid="payment-amount-input"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1 h-11" data-testid="payment-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Date</Label>
            <Input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="mt-1 h-11"
              data-testid="payment-date-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="payment-dialog-save">
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
